// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Rafael Pereira Satyro

import { useState } from 'react';
import { Modal } from '@loa/ui-components';
import { signedDocumentApi } from '../../api/signedDocumentApi';

type DeleteDocumentModalProps = Readonly<{
  open: boolean;
  documentId: string;
  documentName?: string;
  /** Row discriminator ('LOA' | 'COA' | 'UPLOAD') — routes the soft-delete. Defaults to UPLOAD. */
  documentType?: string;
  onClose: () => void;
  onSuccess: () => void;
}>;

/**
 * Red confirmation modal for soft-deleting a signed document (LOA / CoA /
 * advisor upload) from the relationship Documents tab. Self-contained (calls the
 * API, manages its own loading/error state) and reports back via
 * {@code onSuccess}.
 */
export function DeleteDocumentModal({
  open,
  documentId,
  documentName,
  documentType = 'UPLOAD',
  onClose,
  onSuccess,
}: DeleteDocumentModalProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDelete = async () => {
    try {
      setLoading(true);
      setError(null);
      await signedDocumentApi.softDelete(documentType, documentId);
      onSuccess();
    } catch (err) {
      const apiError = err as { response?: { status?: number; data?: { message?: string } } };
      setError(apiError.response?.data?.message || 'Failed to delete document. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (loading) return;
    setError(null);
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose} size="small" variant="warning">
      <Modal.Header icon="trash" title="Delete Document" onClose={handleClose} />
      <Modal.Content>
        <div className="text-[#000] font-sans text-body-01 font-normal leading-normal">
          {documentName ? (
            <>
              Are you sure you want to delete <strong>{documentName}</strong>?
            </>
          ) : (
            'Are you sure you want to delete this document?'
          )}
          {error && (
            <div className="mt-md p-[12px] bg-[#FFF2F3] border border-error-text rounded-lg text-error-text text-body-03">
              {error}
            </div>
          )}
        </div>
      </Modal.Content>
      <Modal.Actions
        secondaryAction={{ label: 'Cancel', onClick: handleClose, disabled: loading, colour: 'red' }}
        primaryAction={{
          label: 'Delete Document',
          onClick: () => { void handleDelete(); },
          loading,
          disabled: loading,
          colour: 'red',
        }}
      />
    </Modal>
  );
}
